import DumbGroupingPanelWithApply from '../Grouping/DumbGroupingPanel.js'
import SourceKnobsPanel from '../Sources/SourceKnobsPanel.js'
import React from 'react'
import SourceTabs from '../SourceTabs.js'
import Actions, { useSelector } from '../../../../Flux/index.js'
import type { State } from '../../../../Flux/Slice'
import type { SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import CollapsibleSidebarElement from './CollapsibleSidebarElement.js'
import SinksSidebarPanel from '../Sinks/SinksSidebarPanel.js'
import { TaxaNavigatorPanel } from '../Sources/TaxaNavigatorPanel.tsx'
import { visualizationSlice } from 'Flux/Slice/Visualization/VisualizationSlice.ts'
import { shallowEqual, useDispatch } from 'react-redux'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'

const selectSourceIds = (s: typeof State): SourceId[] => Object.keys(s.visualization.sources) as SourceId[]

export default function SourceSidebar({
  activeId,
  toggleAside,
  addSource,
  removeSource,
  setActiveId
}: {
  activeId: string | null
  toggleAside: (show: boolean) => void
  addSource: () => void
  removeSource: (id: string) => void
  setActiveId: (id: string | null) => void
}) {
  const sourceIds = useSelector(selectSourceIds, shallowEqual)

  const dispatch = useDispatch()

  const handleSourceRename = (id: SourceId, newName: string) => {
    dispatch(Actions[visualizationSlice.name].updateSourceName({ sourceId: id, name: newName }))
  }

  return (
    <aside className="w-[480px] h-full border-r bg-base-100 flex-col print:hidden flex">
      <div className="p-4 card shadow mb-4">
        <div className="flex items-center justify-between mb-3 card-title">
          <div className={'text-xs'}>
            <button className={'btn btn-xs'} onClick={() => toggleAside(false)}>
              <ChevronLeftIcon className="w-4 h-4" /> Hide
            </button>
          </div>
          <div className="font-semibold">Sources</div>
          <div className="flex gap-2">
            <button data-testid="add-source" className="btn btn-xs btn-outline" onClick={addSource}>
              + Add
            </button>
          </div>
        </div>
        <SourceTabs
          sourceIds={sourceIds}
          activeId={activeId}
          onSelect={(id) => setActiveId(id)}
          onDelete={(id) => removeSource(id)}
          onRename={(id, newName) => handleSourceRename(id, newName)}
        />
      </div>

      <div className="flex-1 overflow-auto">
        {sourceIds.length === 0 ? (
          <div className="text-sm opacity-70 p-4">Select or add a source to configure it.</div>
        ) : (
          sourceIds.map((id) => <SourceSidebarSingle key={id} sourceId={id} activeId={activeId} />)
        )}
      </div>
    </aside>
  )
}

export function SourceSidebarSingle({ sourceId, activeId }: { sourceId: SourceId; activeId: string | null }) {
  const sinkCount = useSelector((s: typeof State) => Object.values(s.visualization.sources[sourceId]?.sinks ?? {}).length)
  const exists = useSelector((s: typeof State) => Boolean(s.visualization.sources[sourceId]))

  if (!exists) return null

  return (
    <div className={sourceId === activeId ? 'block' : 'hidden'}>
      <CollapsibleSidebarElement title={`Sinks (${sinkCount})`}>
        <SinksSidebarPanel sourceId={sourceId} isOpen={activeId === sourceId} />
      </CollapsibleSidebarElement>
      <CollapsibleSidebarElement title={`Groupings`}>
        <DumbGroupingPanelWithApply sourceId={sourceId} />
      </CollapsibleSidebarElement>
      <CollapsibleSidebarElement title={'Settings'}>
        <SourceKnobsPanel sourceId={sourceId} />
      </CollapsibleSidebarElement>
      <CollapsibleSidebarElement title={'Taxonomy'}>
        <TaxaNavigatorPanel sourceId={sourceId} />
      </CollapsibleSidebarElement>
    </div>
  )
}
